// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@guestsupply/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Children, FC, useState } from "react";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    [key: string]: any;
}
interface OwnProps {
    Promotion: React.FC;
    Image: React.FC;
    Description: React.FC;
    Price: React.FC;
    Qty: React.FC;
    Total: React.FC;
    Remove: React.FC;
    Actions: React.FC;
    Notes: React.FC;
    Surcharges: React.FC;
    PoNumber: React.FC;
}
export interface CartLineContainerStyles {
    container?: InjectableCss;
    promotion?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    price?: InjectableCss;
    qty?: InjectableCss;
    total?: InjectableCss;
    actions?: InjectableCss;
    notes?: InjectableCss;
    remove?: InjectableCss;
    surcharges?: InjectableCss;
    poNumber?: InjectableCss;
}

export const cartlineContainerStyles: CartLineContainerStyles = {};

const names = {
    Promotion: "Promotion",
    Image: "Image",
    Description: "Description",
    Price: "Price",
    Qty: "Qty",
    Total: "Total",
    Remove: "Remove",
    Actions: "Actions",
    Notes: "Notes",
    Surcharges: "Surcharges",
    PoNumber: "PoNumber",
};

const Promotion: FC = () => null;
const Image: FC = () => null;
const Description: FC = () => null;
const Price: FC = () => null;
const Qty: FC = () => null;
const Total: FC = () => null;
const Remove: FC = () => null;
const Actions: FC = () => null;
const Notes: FC = () => null;
const Surcharges: FC = () => null;
const PoNumber: FC = () => null;

const CartLineContainer: React.FC<CartLineContainerProps> & OwnProps = ({
    children,
    extendedStyles,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(cartlineContainerStyles, extendedStyles));

    const { Promotion, Image, Description, Price, Qty, Total, Remove, Actions, Notes, Surcharges, PoNumber } =
        React.useMemo(() => {
            const childrenArray = Children.toArray(children) as React.ReactElement[];
            const MappedChilds = childrenArray.reduce((results, current) => {
                const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
                if (currentName) {
                    results[currentName] = current;
                }
                return results;
            }, {} as Record<string, React.ReactElement>);
            const slots = Object.entries(names).reduce((results, [key, value], idx) => {
                results[value] = MappedChilds[value] || null;
                return results;
            }, {} as Record<string, React.ReactElement | null>);

            return slots;
        }, [children]);

    return (
        <>
            <StyledWrapper {...styles.promotion}>{Promotion?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.container} {...otherProps}>
                <StyledWrapper {...styles.image}>{Image?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.description}>{Description?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.price}>{Price?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.qty}>{Qty?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.total}>{Total?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.remove}>{Remove?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.actions}>{Actions?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.notes}>{Notes?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.surcharges}>{Surcharges?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.poNumber}>{PoNumber?.props?.children}</StyledWrapper>
            </StyledWrapper>
        </>
    );
};

CartLineContainer.Promotion = Promotion;
CartLineContainer.Image = Image;
CartLineContainer.Description = Description;
CartLineContainer.Price = Price;
CartLineContainer.Qty = Qty;
CartLineContainer.Total = Total;
CartLineContainer.Remove = Remove;
CartLineContainer.Actions = Actions;
CartLineContainer.Notes = Notes;
CartLineContainer.Surcharges = Surcharges;
CartLineContainer.PoNumber = PoNumber;

CartLineContainer.Promotion.displayName = names.Promotion;
CartLineContainer.Image.displayName = names.Image;
CartLineContainer.Description.displayName = names.Description;
CartLineContainer.Price.displayName = names.Price;
CartLineContainer.Qty.displayName = names.Qty;
CartLineContainer.Total.displayName = names.Total;
CartLineContainer.Remove.displayName = names.Remove;
CartLineContainer.Actions.displayName = names.Actions;
CartLineContainer.Notes.displayName = names.Notes;
CartLineContainer.Surcharges.displayName = names.Surcharges;
CartLineContainer.PoNumber.displayName = names.PoNumber;

export default CartLineContainer;
